/**
 * Carrier MCP — v1.1 backlog tool registrations + smoke-only S6 tools.
 *
 * These are OCS methods confirmed live in the eSIMVault API docs but not part
 * of the original v1 server set. Wired here to close coverage gaps from the
 * 2026-05-12 forensic audit.
 *
 * carrier_webhook_config is a real read-only tool and is registered
 * unconditionally. resolve_cell_location was merged into
 * get_subscriber_location_by_cell_id. CARRIER_AUDIT_STUBS_ENABLED is not a gate.
 *
 * Scope assignments:
 *   write  — affect_subscriber_phone_number, modify_subscriber_mobile_plan,
 *             modify_subscriber_package_active_period, modify_subscriber_voip_plan,
 *             push_steering_to_subscriber, change_network_profile_of_location_zone
 *   read   — get_subscriber_location_by_cell_id, list_destination_lists,
 *             carrier_webhook_config, list_subscriber_voip_tariff, list_voip_tariff_rule
 *   admin  — reset_subscriber_gz_counter
 *
 * Schema notes:
 *   - All ICCID-accepting tools use the subscriber-record lookup cache pattern
 *     (resolveSubscriberByIccid) for IMSI resolution where required.
 *   - pushSteeringToSubs is listed as "write" in the live docs; marked write here
 *     pending confirmation from eSIMVault support (gap G-04).
 *   - resetSubsGzCounter is admin-scoped; usage counter resets are irreversible.
 */

import { z } from "zod";
import type { McpServer } from "@modelcontextprotocol/sdk/server/mcp.js";
import { OcsClient } from "./client.js";
import {
  wrapHandler,
  getDefaultResellerId,
  type ToolContext,
} from "./tools.js";
import type { ToolScope } from "./types.js";
import { activePeriodFromPackages, packageActivePeriodParams } from "@carrier/ocs-client";

const DRY_RUN_FIELD = {
  dry_run: z
    .boolean()
    .optional()
    .describe(
      "If true, do not call OCS — return the would-be request for confirmation",
    ),
};

/**
 * Normalise radio type aliases to the canonical Bridge4IP value.
 *
 * Bridge4IP GeoSense accepts "NB-IoT" (with hyphen, mixed case) per its inputs
 * table. Upstream sources (e.g. Relay LU webhook events, device firmware logs)
 * commonly emit "NBIOT", "nb-iot", or "nbiot". All four are mapped here to
 * "NB-IoT". All other values (2G / 3G / 4G / 5G) pass through unchanged.
 *
 * Canonical: "NB-IoT" (confirmed from packages/ocs-spec/ocs-methods.json).
 */
function normalizeRadioType(value: string): string {
  if (/^nb-?iot$/i.test(value)) return "NB-IoT";
  return value;
}

const VALID_RADIO_TYPES = new Set(["2G", "3G", "4G", "5G", "NB-IoT"]);

/** Scope lookup for the v1.1 backlog tools + implemented v1.2 tools. */
export const BACKLOG_TOOL_SCOPES: Record<string, ToolScope> = {
  affect_subscriber_phone_number: "write",
  carrier_webhook_config: "read",
  change_network_profile_of_location_zone: "write",
  get_subscriber_location_by_cell_id: "read",
  list_destination_lists: "read",
  list_subscriber_voip_tariff: "read",
  list_voip_tariff_rule: "read",
  modify_subscriber_mobile_plan: "write",
  modify_subscriber_package_active_period: "write",
  modify_subscriber_voip_plan: "write",
  push_steering_to_subscriber: "write",
  reset_subscriber_gz_counter: "admin",
};

export function registerAllBacklogTools(
  server: McpServer,
  ctx: ToolContext,
): void {
  // =========================================================================
  // 1. AFFECT SUBSCRIBER PHONE NUMBER
  //    OCS methods: affectSubscriberFakePhoneNumber / affectSubscriberRealPhoneNumber
  //    Gap: G-08 (MEDIUM). Merges two OCS methods behind one MCP tool.
  // =========================================================================
  server.registerTool(
    "affect_subscriber_phone_number",
    {
      title: "Assign Phone Number to Subscriber",
      description:
        "Use this to assign a phone number (MSISDN) to a subscriber. Supports both fake/test MSISDNs " +
        "and real production MSISDNs via the `phone_type` parameter. Required for MSISDN assignment " +
        "workflows before activating voice services. " +
        "Params: `iccid` (subscriber identifier), `phone_number` (E.164 format, e.g. +31612345678), " +
        "`phone_type` ('fake' for test/dev, 'real' for production). " +
        "Returns: updated subscriber record with the new MSISDN. " +
        "Do NOT use this to check a subscriber's current MSISDN — use `get_subscriber` instead.",
      inputSchema: {
        iccid: z.string().describe("The subscriber ICCID (20-digit ICC identifier)"),
        phone_number: z
          .string()
          .describe("E.164 phone number to assign (e.g. +31612345678)"),
        phone_type: z
          .enum(["fake", "real"])
          .describe(
            "'fake' assigns a test/dev MSISDN (affectSubscriberFakePhoneNumber in OCS); " +
            "'real' assigns a production MSISDN (affectSubscriberRealPhoneNumber in OCS)",
          ),
        ...DRY_RUN_FIELD,
      },
      annotations: { destructiveHint: true },
    },
    wrapHandler(
      "affect_subscriber_phone_number",
      "affectSubscriberFakePhoneNumber/affectSubscriberRealPhoneNumber",
      BACKLOG_TOOL_SCOPES["affect_subscriber_phone_number"]!,
      ctx,
      async ({ iccid, phone_number, phone_type }: { iccid: string; phone_number: string; phone_type: string }, token: string) => {
        const ocsMethod =
          phone_type === "fake"
            ? "affectSubscriberFakePhoneNumber"
            : "affectSubscriberRealPhoneNumber";
        const client = new OcsClient(ctx.env.CARRIER_OCS_BASE_URL, token);
        const result = await client.call(ocsMethod, { subscriber: iccid, phoneNumber: phone_number });
        return {
          content: [{ type: "text", text: JSON.stringify(result, null, 2) }],
        };
      },
    ),
  );

  // =========================================================================
  // 2. GET SUBSCRIBER LOCATION BY CELL ID
  //    OCS method: getSubscriberLocationByCellId (Bridge4IP GeoSense)
  //    Gap: G-09 (MEDIUM). Takes a cell tuple — no subscriber identifier needed.
  // =========================================================================
  server.registerTool(
    "get_subscriber_location_by_cell_id",
    {
      title: "Get Location by Cell ID (GeoSense)",
      description:
        "Powered by Bridge4IP GeoSense — cell-level location resolution with sub-cell accuracy where available. " +
        "Use this to resolve a cell tower tuple (radio type + MCC + MNC + LAC + optional cellId) " +
        "to a latitude/longitude estimate. " +
        "No subscriber identifier required — caller supplies cell parameters directly. " +
        "Useful for fraud detection, roaming cost attribution, and network troubleshooting when " +
        "you have raw cell info from an external source (e.g. a Relay LU webhook event). " +
        "Params: `radio_type` ('2G'|'3G'|'4G'|'5G'|'NB-IoT'; also accepts 'NBIOT'|'nb-iot'|'nbiot' — normalised to 'NB-IoT'), `mcc` (int), `mnc` (int), " +
        "`lac` (int), `cell_id` (int, optional but strongly recommended for accuracy), " +
        "`signal_strength` (number dBm, optional). " +
        "Returns: { latitude, longitude, accuracy } — accuracy is median error in meters at 50% confidence. " +
        "Without cell_id accuracy degrades significantly (>10 km). " +
        "Do NOT use this for bulk fleet location sweeps — one OCS call per cell tower; " +
        "use `audit_network_coverage` for fleet-level analysis instead.",
      inputSchema: {
        radio_type: z
          .string()
          .transform(normalizeRadioType)
          .refine((v) => VALID_RADIO_TYPES.has(v), {
            message: "radio_type must be one of: 2G, 3G, 4G, 5G, NB-IoT (also accepts NBIOT, nb-iot, nbiot)",
          })
          .describe("Radio access technology type: '2G'|'3G'|'4G'|'5G'|'NB-IoT'. Also accepts 'NBIOT'|'nb-iot'|'nbiot' — normalised to 'NB-IoT'."),
        mcc: z.number().int().describe("Mobile Country Code (e.g. 250 for Russia, 234 for UK)"),
        mnc: z.number().int().describe("Mobile Network Code"),
        lac: z.number().int().describe("Location Area Code"),
        cell_id: z
          .number()
          .int()
          .optional()
          .describe("Cell tower ID — strongly recommended for accuracy"),
        signal_strength: z
          .number()
          .optional()
          .describe("Signal strength in dBm (e.g. -89). Optional, improves accuracy."),
      },
      annotations: { readOnlyHint: true },
    },
    wrapHandler(
      "get_subscriber_location_by_cell_id",
      "getSubscriberLocationByCellId",
      BACKLOG_TOOL_SCOPES["get_subscriber_location_by_cell_id"]!,
      ctx,
      async (
        {
          radio_type,
          mcc,
          mnc,
          lac,
          cell_id,
          signal_strength,
        }: {
          radio_type: string; // normalised by normalizeRadioType — always a VALID_RADIO_TYPES member
          mcc: number;
          mnc: number;
          lac: number;
          cell_id?: number;
          signal_strength?: number;
        },
        token: string,
      ) => {
        const client = new OcsClient(ctx.env.CARRIER_OCS_BASE_URL, token);
        const params: Record<string, unknown> = { radioType: radio_type, mcc, mnc, lac };
        if (cell_id !== undefined) params.cellId = cell_id;
        if (signal_strength !== undefined) params.signalStrength = signal_strength;
        const result = await client.call("getSubscriberLocationByCellId", params);
        return {
          content: [{ type: "text", text: JSON.stringify(result, null, 2) }],
        };
      },
    ),
  );

  // =========================================================================
  // 3. LIST DESTINATION LISTS
  //    OCS method: listDetailedDestinationList
  //    Gap: G-13 (MEDIUM). The existing list_destination_prefixes only reads
  //    prefixes WITHIN a known list ID; this tool returns the list catalog itself.
  // =========================================================================
  server.registerTool(
    "list_destination_lists",
    {
      title: "List Destination Lists",
      description:
        "Use this to retrieve the full catalog of destination lists available to this reseller. " +
        "A destination list is a named set of phone number prefixes (country codes) that control " +
        "which numbers a subscriber may call on a voice/SMS package. " +
        "Returns: array of destination list records, each with `id`, `name`, and prefix count. " +
        "Do NOT use this to read the prefixes inside a specific list — use `list_destination_prefixes` " +
        "with a known `destinationListId` for that. " +
        "Do NOT use this for data-only eSIM products without MOC voice; destination lists only " +
        "apply to packages with voice/SMS allowances.",
      inputSchema: {
        resellerId: z
          .number()
          .optional()
          .describe("Reseller ID (omit to use the token owner's reseller)"),
      },
      annotations: { readOnlyHint: true },
    },
    wrapHandler(
      "list_destination_lists",
      "listDetailedDestinationList",
      BACKLOG_TOOL_SCOPES["list_destination_lists"]!,
      ctx,
      async ({ resellerId }: { resellerId?: number }, token: string) => {
        const id =
          resellerId ??
          (await (async () => {
            const client = new OcsClient(ctx.env.CARRIER_OCS_BASE_URL, token);
            const info = await client.call<{ id?: number }>("getResellerInfo", {});
            const resolvedId = info?.id;
            if (typeof resolvedId !== "number") {
              throw new Error("Could not determine resellerId from getResellerInfo");
            }
            return resolvedId;
          })());
        const client = new OcsClient(ctx.env.CARRIER_OCS_BASE_URL, token);
        const result = await client.call("listDetailedDestinationList", id);
        return {
          content: [{ type: "text", text: JSON.stringify(result, null, 2) }],
        };
      },
    ),
  );

  // =========================================================================
  // 4. MODIFY SUBSCRIBER MOBILE PLAN
  //    OCS method: modifySubscriberMobilePlan
  //    Gap: G-06 (HIGH). Needed to change which pricing plan a subscriber is billed under.
  // =========================================================================
  server.registerTool(
    "modify_subscriber_mobile_plan",
    {
      title: "Modify Subscriber Mobile Plan",
      description:
        "Use this to change the mobile pricing plan assigned to a specific subscriber. " +
        "The mobile plan determines per-country rates for data, voice, and SMS. Changing the plan " +
        "takes effect immediately on the next OCS rating cycle. " +
        "Params: `iccid` (subscriber identifier), `mobile_plan_id` (integer plan ID — obtain valid " +
        "plan IDs from the OCS reseller settings or `get_tariff`). " +
        "Returns: updated subscriber record confirming the new plan assignment. " +
        "Do NOT use this to change package allowances — use `modify_package_limits` for that. " +
        "Do NOT use this to change account-level pricing — this only affects the individual subscriber.",
      inputSchema: {
        iccid: z.string().describe("The subscriber ICCID (20-digit ICC identifier)"),
        mobile_plan_id: z
          .number()
          .describe("The mobile pricing plan ID to assign to this subscriber"),
        ...DRY_RUN_FIELD,
      },
      annotations: { destructiveHint: true },
    },
    wrapHandler(
      "modify_subscriber_mobile_plan",
      "modifySubscriberMobilePlan",
      BACKLOG_TOOL_SCOPES["modify_subscriber_mobile_plan"]!,
      ctx,
      async ({ iccid, mobile_plan_id }: { iccid: string; mobile_plan_id: number }, token: string) => {
        const client = new OcsClient(ctx.env.CARRIER_OCS_BASE_URL, token);
        // `subscriber` is not a field this method knows. OCS takes the
        // identifier under `subscriberId`, and that key holds a
        // common.SubscriberId OBJECT — so the ICCID nests inside it rather
        // than being written into a numeric id field. See
        // subscriberPlanParams in @carrier/ocs-client for the live probe.
        const result = await client.call(
          "modifySubscriberMobilePlan",
          { subscriberId: { iccid }, mobilePlanId: mobile_plan_id },
        );
        return {
          content: [{ type: "text", text: JSON.stringify(result, null, 2) }],
        };
      },
    ),
  );

  // =========================================================================
  // 5. MODIFY SUBSCRIBER PACKAGE ACTIVE PERIOD
  //    OCS method: modifySubscriberPrepaidPackageActivePeriod
  //    Gap: G-07 (HIGH). Controls when a package becomes active on a subscriber.
  // =========================================================================
  server.registerTool(
    "modify_subscriber_package_active_period",
    {
      title: "Modify Subscriber Package Active Period",
      description:
        "Use this to change the start and/or end date of a prepaid package's active period for a " +
        "specific subscriber. This controls WHEN the package runs, not what it contains. " +
        "Useful for scheduling packages in advance (e.g. activate on arrival date) or extending " +
        "a package that would otherwise expire while the subscriber is still travelling. " +
        "Params: `iccid` (subscriber identifier), `package_id` (from `list_subscriber_packages`), " +
        "`start_date` (ISO 8601 date, optional), `end_date` (ISO 8601 date, optional). " +
        "Returns: updated package record with new active period. " +
        "Do NOT use this to change a package's data/voice allowance — use `modify_package_limits`. " +
        "Do NOT use this to change the expiry date of a package — use `modify_package_expiry`.",
      inputSchema: {
        iccid: z.string().describe("The subscriber ICCID (20-digit ICC identifier)"),
        package_id: z
          .number()
          .describe("The subscriber package ID (from list_subscriber_packages)"),
        start_date: z
          .string()
          .optional()
          .describe("New start date in ISO 8601 format (YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss)"),
        end_date: z
          .string()
          .optional()
          .describe("New end date in ISO 8601 format (YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss)"),
        comment: z
          .string()
          .optional()
          .describe(
            "Audit note stored on the OCS package record. OCS requires this " +
              "field, so a default is sent when it is omitted.",
          ),
        ...DRY_RUN_FIELD,
      },
      annotations: { destructiveHint: true },
    },
    wrapHandler(
      "modify_subscriber_package_active_period",
      "modifySubscriberPrepaidPackageActivePeriod",
      BACKLOG_TOOL_SCOPES["modify_subscriber_package_active_period"]!,
      ctx,
      async ({ iccid, package_id, start_date, end_date, comment }: { iccid: string; package_id: number; start_date?: string; end_date?: string; comment?: string }, token: string) => {
        // OCS accepts exactly four properties here — newActivationDateUtc,
        // packageId, newExpirationDateUtc, comment — and REQUIRES all four.
        // `subscriber`, `startDate` and `endDate` are all rejected outright,
        // so this handler's old shape failed on every call. A one-sided
        // change reads the window OCS holds today and resends the other bound
        // unchanged; the package id alone identifies the row.
        const client = new OcsClient(ctx.env.CARRIER_OCS_BASE_URL, token);
        let current: { activation?: string; expiration?: string } | undefined;
        if (start_date === undefined || end_date === undefined) {
          const listed = await client.call("listSubscriberPrepaidPackages", { iccid });
          current = activePeriodFromPackages(listed, package_id);
          if (!current) {
            throw new Error(`Package ${package_id} is not on subscriber ${iccid}`);
          }
        }
        const params = packageActivePeriodParams(package_id, {
          startDate: start_date,
          endDate: end_date,
          comment,
          current,
        });
        const result = await client.call(
          "modifySubscriberPrepaidPackageActivePeriod",
          params,
        );
        return {
          content: [{ type: "text", text: JSON.stringify(result, null, 2) }],
        };
      },
    ),
  );

  // =========================================================================
  // 6. MODIFY SUBSCRIBER VOIP PLAN
  //    OCS method: modifySubscriberVoipPlan
  //    Gap: G-11 (MEDIUM). Parallel to modify_subscriber_mobile_plan for VoIP.
  // =========================================================================
  server.registerTool(
    "modify_subscriber_voip_plan",
    {
      title: "Modify Subscriber VoIP Plan",
      description:
        "Use this to change the VoIP pricing plan assigned to a specific subscriber. " +
        "VoIP plans control billing rates for VoIP calls made through the OCS platform, " +
        "separate from the standard mobile plan's call rates. " +
        "Params: `iccid` (subscriber identifier), `voip_plan_id` (integer VoIP plan ID from " +
        "the OCS reseller settings). " +
        "Returns: updated subscriber record confirming the new VoIP plan. " +
        "Do NOT use this for mobile (non-VoIP) plan changes — use `modify_subscriber_mobile_plan`. " +
        "For data-only eSIM products without VoIP services this tool has no effect.",
      inputSchema: {
        iccid: z.string().describe("The subscriber ICCID (20-digit ICC identifier)"),
        voip_plan_id: z
          .number()
          .describe("The VoIP pricing plan ID to assign to this subscriber"),
        ...DRY_RUN_FIELD,
      },
      annotations: { destructiveHint: true },
    },
    wrapHandler(
      "modify_subscriber_voip_plan",
      "modifySubscriberVoipPlan",
      BACKLOG_TOOL_SCOPES["modify_subscriber_voip_plan"]!,
      ctx,
      async ({ iccid, voip_plan_id }: { iccid: string; voip_plan_id: number }, token: string) => {
        const client = new OcsClient(ctx.env.CARRIER_OCS_BASE_URL, token);
        // Same shape as modify_subscriber_mobile_plan: `subscriberId` is a
        // common.SubscriberId object, so the ICCID nests inside it.
        const result = await client.call(
          "modifySubscriberVoipPlan",
          { subscriberId: { iccid }, voipPlanId: voip_plan_id },
        );
        return {
          content: [{ type: "text", text: JSON.stringify(result, null, 2) }],
        };
      },
    ),
  );

  // =========================================================================
  // 7. PUSH STEERING TO SUBSCRIBER
  //    OCS method: pushSteeringToSubs
  //    Gap: G-04 (HIGH). Must be called AFTER modify_subscriber_steering_list
  //    to push the new OPLMN list to the physical device.
  // =========================================================================
  server.registerTool(
    "push_steering_to_subscriber",
    {
      title: "Push Steering List to Subscriber Device",
      description:
        "Use this AFTER `modify_subscriber_steering_list` to actively push the updated OPLMN " +
        "(operator preference list) to the subscriber's physical eSIM/SIM. " +
        "Without this call, the steering list assignment change is recorded in OCS but the device " +
        "continues using the old operator preference list until it performs a network re-registration. " +
        "This is required for immediate operator switching (e.g. steering a subscriber away from " +
        "an expensive roaming partner in real time). " +
        "Params: `iccid` (subscriber identifier). " +
        "Returns: push confirmation from OCS with delivery status. " +
        "Do NOT call this without first calling `modify_subscriber_steering_list` — pushing without " +
        "an assigned steering list is a no-op and wastes an OCS call.",
      inputSchema: {
        iccid: z.string().describe("The subscriber ICCID (20-digit ICC identifier)"),
        ...DRY_RUN_FIELD,
      },
      annotations: { destructiveHint: true },
    },
    wrapHandler(
      "push_steering_to_subscriber",
      "pushSteeringToSubs",
      BACKLOG_TOOL_SCOPES["push_steering_to_subscriber"]!,
      ctx,
      async ({ iccid }: { iccid: string }, token: string) => {
        // OCS pushSteeringToSubs takes a common.SubscriberId object at the top
        // level — verified live 2026-08-24: {"subscriber": "<iccid>"} fails
        // with "Unrecognized field 'subscriber' ... 6 known properties:
        // multiImsi, iccid, imsi, activationCode, msisdn, subscriberId". The
        // identifier IS the ICCID; the field OCS wants is `iccid`, so no
        // subscriber-record lookup is needed to build this request. This is
        // the port of the mcp-server fix — the two surfaces had diverged.
        const client = new OcsClient(ctx.env.CARRIER_OCS_BASE_URL, token);
        const result = await client.call(
          "pushSteeringToSubs",
          { iccid },
        );
        return {
          content: [{ type: "text", text: JSON.stringify(result, null, 2) }],
        };
      },
    ),
  );

  // =========================================================================
  // 8. RESET SUBSCRIBER GZ COUNTER
  //    OCS method: resetSubsGzCounter
  //    Gap: G-10 (MEDIUM). Resets usage counters — used after billing disputes.
  //    Admin scope — irreversible operation.
  // =========================================================================
  server.registerTool(
    "reset_subscriber_gz_counter",
    {
      title: "Reset Subscriber Green Zone Counter",
      description:
        "ADMIN: Use this to reset the Green Zone (Greenzone) volume counter for a subscriber. " +
        "The Green Zone counter tracks bytes consumed on the reseller-defined whitelist of " +
        "hosts/IPs after the subscriber's bundle is depleted — NOT the Diameter Gz/Gy accounting " +
        "interface. Typically used after a billing dispute or test-cycle reset. " +
        "This operation is IRREVERSIBLE — volumeOnGZ is permanently zeroed. " +
        "Params: `iccid` (subscriber identifier). " +
        "Returns: new counter state with volumeOnGZ (bytes, normally 0 after reset), " +
        "lastResetDate, lastUpdateDate. " +
        "Always call `get_subscriber` with `with_gz_counter=true` first to capture the snapshot. " +
        "Do NOT use this to pause data usage — use `set_subscriber_traffic_restrictions` with " +
        "`dataAllowed=false` instead.",
      inputSchema: {
        iccid: z.string().describe("The subscriber ICCID (20-digit ICC identifier)"),
        ...DRY_RUN_FIELD,
      },
      annotations: { destructiveHint: true },
    },
    wrapHandler(
      "reset_subscriber_gz_counter",
      "resetSubsGzCounter",
      BACKLOG_TOOL_SCOPES["reset_subscriber_gz_counter"]!,
      ctx,
      async ({ iccid }: { iccid: string }, token: string) => {
        // Same defect and same fix as pushSteeringToSubs: OCS resetSubsGzCounter
        // takes a common.SubscriberId object at the top level, and the field it
        // wants for an ICCID is `iccid`, not `subscriber` — verified live
        // 2026-08-24 against the same SubscriberId 6-property enumeration.
        const client = new OcsClient(ctx.env.CARRIER_OCS_BASE_URL, token);
        const result = await client.call("resetSubsGzCounter", { iccid });
        return {
          content: [{ type: "text", text: JSON.stringify(result, null, 2) }],
        };
      },
    ),
  );

  // =========================================================================
  // 10. CARRIER WEBHOOK CONFIG — reads relay + notification flags from
  //     getResellerInfo.trafficInfo and getResellerInfo.notificationInfo.
  //     Audit gap S-04 in UNUSED-FEATURES.md — now implemented.
  //
  //     trafficInfo shape (live probe against reseller 1170, 2026-05-21):
  //       { relayGy: boolean, relayCallSms: boolean, relayLU: boolean, relayVoIP: boolean }
  //     notificationInfo shape: passed through as-is (z.record(z.unknown())) because
  //       the exact keys vary by OCS configuration and are not documented upstream.
  // =========================================================================

  // carrier_webhook_config is now a real tool — registered unconditionally
  // (no CARRIER_AUDIT_STUBS_ENABLED guard needed after implementation).
  server.registerTool(
    "carrier_webhook_config",
    {
      title: "Carrier Webhook / Relay Flag Status",
      description:
        "Use this to check the current Bridge4IP webhook and relay flag configuration for " +
        "this reseller. Returns the active state of all four traffic relay flags: " +
        "Relay LU (Update Location — cell tower changes, closest to country-change signal), " +
        "Relay Gy (mobile data usage events), Relay VoIP (VoIP usage events), " +
        "Relay calls+SMS (call and SMS events). " +
        "Also returns which notification webhooks are enabled (prepaid package usage threshold, " +
        "reseller low credit, ES2 eSIM status, recurring packages) as a passthrough object. " +
        "All relay/notification configuration is done in the OCS portal UI — this tool is " +
        "READ-ONLY and reflects current state. " +
        "Relay LU is the highest-value flag: when active, Bridge4IP pushes real-time Update " +
        "Location events (mcc, mnc, lac, cellId) to the configured HTTP endpoint.",
      inputSchema: {
        reseller_id: z
          .number()
          .int()
          .optional()
          .describe("Reseller ID (omit to use the token owner's reseller)"),
      },
      annotations: { readOnlyHint: true },
    },
    wrapHandler(
      "carrier_webhook_config",
      "getResellerInfo",
      BACKLOG_TOOL_SCOPES["carrier_webhook_config"]!,
      ctx,
      async ({ reseller_id }, token) => {
        const client = new OcsClient(ctx.env.CARRIER_OCS_BASE_URL, token);

        // Resolve reseller ID: explicit arg → token owner's reseller
        let resellerId = reseller_id;
        if (resellerId === undefined) {
          resellerId = await getDefaultResellerId(ctx.env, token);
        }

        const params: Record<string, unknown> = {};
        if (resellerId !== undefined) params.id = resellerId;

        const raw = await client.call<{
          id?: number;
          trafficInfo?: {
            relayGy?: boolean;
            relayCallSms?: boolean;
            relayLU?: boolean;
            relayVoIP?: boolean;
          };
          notificationInfo?: Record<string, unknown>;
        }>("getResellerInfo", params);

        const traffic = raw.trafficInfo ?? {};
        const notification = raw.notificationInfo ?? {};

        const result = {
          resellerId: raw.id ?? resellerId,
          traffic: {
            relayGy: traffic.relayGy === true,
            relayCallSms: traffic.relayCallSms === true,
            relayLU: traffic.relayLU === true,
            relayVoIP: traffic.relayVoIP === true,
          },
          notification,
        };

        return {
          content: [{ type: "text" as const, text: JSON.stringify(result, null, 2) }],
        };
      },
    ),
  );

  // =========================================================================
  // 11. LIST SUBSCRIBER VOIP TARIFF (S6-T1 / CAR-105)
  //     OCS method: listSubscriberVoipTariff (smoke-verified; not on public docs)
  //     Request: optional { resellerId }. Empty object accepted.
  // =========================================================================
  server.registerTool(
    "list_subscriber_voip_tariff",
    {
      title: "List VoIP Tariffs",
      description:
        "Use this to list VoIP tariff definitions available to a reseller. " +
        "Despite the OCS method name (listSubscriberVoipTariff), this is a reseller-level " +
        "catalog listing — not a per-ICCID lookup. Use results (or get_reseller_info " +
        "chargingInfo.voipPlan.id) to feed `list_voip_tariff_rule` for rate detail. " +
        "Params: `reseller_id` (integer, optional — omit to use the token owner's reseller). " +
        "Returns: OCS VoIP tariff records (may be empty when no VoIP catalog is configured). " +
        "Do NOT use this for mobile wholesale rates — use `get_tariff` (getCustomerTariff). " +
        "Do NOT use this to assign a VoIP plan — use `modify_subscriber_voip_plan`.",
      inputSchema: {
        reseller_id: z
          .number()
          .int()
          .positive()
          .optional()
          .describe("Reseller ID (omit to list tariffs for the token owner's reseller)"),
      },
      annotations: { readOnlyHint: true },
    },
    wrapHandler(
      "list_subscriber_voip_tariff",
      "listSubscriberVoipTariff",
      BACKLOG_TOOL_SCOPES["list_subscriber_voip_tariff"]!,
      ctx,
      async ({ reseller_id }: { reseller_id?: number }, token: string) => {
        const client = new OcsClient(ctx.env.CARRIER_OCS_BASE_URL, token);
        const params: Record<string, unknown> = {};
        if (reseller_id !== undefined) {
          params.resellerId = reseller_id;
        }
        const result = await client.call("listSubscriberVoipTariff", params);
        return {
          content: [{ type: "text" as const, text: JSON.stringify(result, null, 2) }],
        };
      },
    ),
  );

  // =========================================================================
  // 12. LIST VOIP TARIFF RULE (S6-T2 / CAR-105)
  //     OCS method: listVoipTariffRule — bare integer voip plan/tariff id
  //     (same primitive-body pattern as getCustomerTariff).
  // =========================================================================
  server.registerTool(
    "list_voip_tariff_rule",
    {
      title: "List VoIP Tariff Rules",
      description:
        "Use this to list the rate rules (costs) for a specific VoIP plan/tariff. " +
        "OCS expects a bare integer plan id in the request body (not an object). " +
        "Obtain plan ids from `get_reseller_info` → chargingInfo.voipPlan.id, or from " +
        "`list_subscriber_voip_tariff`. " +
        "Params: `voip_plan_id` (integer, required — VoIP plan/tariff id). " +
        "Returns: VoIP tariff rule rows for that plan, or an OCS error when the id is unknown. " +
        "Do NOT use this for mobile (non-VoIP) wholesale rates — use `get_tariff`. " +
        "Do NOT use this to change a subscriber's VoIP plan — use `modify_subscriber_voip_plan`.",
      inputSchema: {
        voip_plan_id: z
          .number()
          .int()
          .positive()
          .describe(
            "VoIP plan/tariff ID (bare integer to OCS listVoipTariffRule; " +
              "from get_reseller_info chargingInfo.voipPlan.id or list_subscriber_voip_tariff)",
          ),
      },
      annotations: { readOnlyHint: true },
    },
    wrapHandler(
      "list_voip_tariff_rule",
      "listVoipTariffRule",
      BACKLOG_TOOL_SCOPES["list_voip_tariff_rule"]!,
      ctx,
      async ({ voip_plan_id }: { voip_plan_id: number }, token: string) => {
        const client = new OcsClient(ctx.env.CARRIER_OCS_BASE_URL, token);
        // Bare integer body — same pattern as getCustomerTariff / listSponsor.
        const result = await client.call("listVoipTariffRule", voip_plan_id);
        return {
          content: [{ type: "text" as const, text: JSON.stringify(result, null, 2) }],
        };
      },
    ),
  );

  // =========================================================================
  // 13. CHANGE NETWORK PROFILE OF LOCATION ZONE (S6-T3 / CAR-105)
  //     OCS method: changeNetworkProfileOfLocationZone
  //     Request: { locationZoneId, networkProfileId }
  // =========================================================================
  server.registerTool(
    "change_network_profile_of_location_zone",
    {
      title: "Change Network Profile of Location Zone",
      description:
        "Use this to attach or change the network profile on an existing location zone. " +
        "A network profile defines sponsor/roaming configuration used when the zone is " +
        "referenced by package templates. " +
        "Params: `location_zone_id` (integer from list_detailed_location_zones), " +
        "`network_profile_id` (integer from list_network_profiles). " +
        "Returns: OCS confirmation of the updated zone/profile binding. " +
        "Do NOT use this to create a new location zone — use `create_location_zone` " +
        "(which requires networkProfileId at creation time). " +
        "Do NOT use this for edit/delete of zone TADIG lists — those remain UI-agent-only (G-19).",
      inputSchema: {
        location_zone_id: z
          .number()
          .int()
          .positive()
          .describe("Location zone ID (locationZoneId / zoneId from list_detailed_location_zones)"),
        network_profile_id: z
          .number()
          .int()
          .positive()
          .describe("Network profile ID (from list_network_profiles)"),
        ...DRY_RUN_FIELD,
      },
      annotations: { destructiveHint: true },
    },
    wrapHandler(
      "change_network_profile_of_location_zone",
      "changeNetworkProfileOfLocationZone",
      BACKLOG_TOOL_SCOPES["change_network_profile_of_location_zone"]!,
      ctx,
      async (
        {
          location_zone_id,
          network_profile_id,
        }: { location_zone_id: number; network_profile_id: number },
        token: string,
      ) => {
        const client = new OcsClient(ctx.env.CARRIER_OCS_BASE_URL, token);
        const result = await client.call("changeNetworkProfileOfLocationZone", {
          locationZoneId: location_zone_id,
          networkProfileId: network_profile_id,
        });
        return {
          content: [{ type: "text" as const, text: JSON.stringify(result, null, 2) }],
        };
      },
    ),
  );
}
